/**
 * Apartsales — Action glyphs.
 *
 * Outline-иконки для строк action-sheet (Apple SF Symbols-стиль):
 * стрelka 1.8pt, rounded caps & joins, `currentColor`. 20×20 viewBox.
 *
 * Используются в:
 *   - MoreActionsSheet на детальной странице юнита (UnitDetailPage)
 *   - OverflowMenu на витринной превью-карточке (UnitCard)
 *
 * Цель — единый visual language Apple, без эмодзи и без рассинхрона
 * между поверхностями.
 */

const G = {
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  fill: 'none' as const,
};

function Svg({ children }: { children: React.ReactNode }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      {children}
    </svg>
  );
}

/** Отправить — стрелка вверх из контейнера. */
export function ShareGlyph() {
  return (
    <Svg>
      <path {...G} d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" />
    </Svg>
  );
}

/** Apartsales Консьерж — speech bubble. */
export function ConciergeGlyph() {
  return (
    <Svg>
      <path
        {...G}
        d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
      />
    </Svg>
  );
}

/** Колокольчик — уведомления (filled = on, outline = off). */
export function BellGlyph({ filled }: { filled: boolean }) {
  if (filled) {
    return (
      <Svg>
        <path d="M12 2a6 6 0 00-6 6v3.5L4 15h16l-2-3.5V8a6 6 0 00-6-6z" fill="currentColor" />
        <path {...G} d="M9.5 18a2.5 2.5 0 005 0" />
      </Svg>
    );
  }
  return (
    <Svg>
      <path {...G} d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0" />
    </Svg>
  );
}

/** PDF / документ со стрелкой загрузки вниз. */
export function PdfGlyph() {
  return (
    <Svg>
      <path {...G} d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <path {...G} d="M14 2v6h6M12 18v-6M9 15l3 3 3-3" />
    </Svg>
  );
}

/** QR-код — три finder-патрона + точки. */
export function QrGlyph() {
  return (
    <Svg>
      <rect {...G} x="3" y="3" width="7" height="7" rx="1" />
      <rect {...G} x="14" y="3" width="7" height="7" rx="1" />
      <rect {...G} x="3" y="14" width="7" height="7" rx="1" />
      <path {...G} d="M14 14h3v3M21 14v3M14 18v3M17 21h4" />
    </Svg>
  );
}

/** Сравнить — два столбика разной высоты (та же геометрия что в compare-chip). */
export function CompareGlyph() {
  return (
    <Svg>
      <path {...G} d="M7 4v16M17 8v12M3 20h8M13 20h8" />
    </Svg>
  );
}

/** Копировать / link — две цепи. */
export function LinkGlyph() {
  return (
    <Svg>
      <path {...G} d="M10 13a5 5 0 007.07 0l3-3a5 5 0 00-7.07-7.07l-1.5 1.5" />
      <path {...G} d="M14 11a5 5 0 00-7.07 0l-3 3a5 5 0 007.07 7.07l1.5-1.5" />
    </Svg>
  );
}

/** Сохранить в коллекцию — bookmark. */
export function BookmarkGlyph() {
  return (
    <Svg>
      <path {...G} d="M19 21l-7-5-7 5V5a2 2 0 012-2h10a2 2 0 012 2z" />
    </Svg>
  );
}

/** Скрыть — eye перечёркнутый. */
export function EyeOffGlyph() {
  return (
    <Svg>
      <path
        {...G}
        d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"
      />
      <path {...G} d="M1 1l22 22" />
    </Svg>
  );
}

/** Пожаловаться — треугольник с восклицательным. */
export function AlertGlyph() {
  return (
    <Svg>
      <path {...G} d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <path {...G} d="M12 9v4M12 17h.01" />
    </Svg>
  );
}

/** Galka — для toggle-state checkmark. */
export function CheckGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12.5l4.5 4.5L19 7.5"
        stroke="var(--color-brand-blue)"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Sparkles — discovery / AI-suggested similar items / smart features.
 *  Три искры разного размера, как в Apple для AI и smart actions. */
export function SparklesGlyph({ filled }: { filled?: boolean }) {
  const fill = filled ? 'currentColor' : 'none';
  return (
    <Svg>
      {/* big sparkle (center-right) */}
      <path
        {...G}
        fill={fill}
        d="M14 4l1.2 3.2 3.2 1.2-3.2 1.2L14 12.8l-1.2-3.2-3.2-1.2 3.2-1.2z"
      />
      {/* small sparkle (top-left) */}
      <path
        {...G}
        fill={fill}
        d="M6 13l.7 1.8 1.8.7-1.8.7L6 18l-.7-1.8L3.5 15.5l1.8-.7z"
      />
      {/* tiny sparkle (bottom-right) */}
      <path
        {...G}
        fill={fill}
        d="M17 17l.5 1.3 1.3.5-1.3.5-.5 1.3-.5-1.3L15.2 19l1.3-.5z"
      />
    </Svg>
  );
}
